import { useDropzone } from 'react-dropzone';
import { useFormikContext, useField } from 'formik';
import { Link } from 'react-router-dom';
import { InputLabel, Card, CircularProgress, Grid, Typography, Icon } from '@material-ui/core';

// local imports
import { ImageUtils } from 'app/utils';
import clsx from 'clsx';

const AddPhoto = ({
  loading,
  name,
  selectedFiles,
  setSelectedFiles,
  label,
  removeFile,
  ...props
}) => {
  const { setFieldValue } = useFormikContext();
  const [, meta] = useField({ name, ...props });
  const invalid = !!meta.error && !!meta.touched;

  // onDrop - drop images function
  const onDrop = files => {
    handleAcceptedFiles(files);
    setSelectedFiles(files);
  };

  // Dropzone hook
  const { getRootProps, getInputProps, inputRef, fileRejections } = useDropzone({
    onDrop,
    accept: '.png,.jpeg,.jpg',
    multiple: false,
    maxFiles: 1,
    maxSize: 5000000
  });

  // convert base64 string and create image preview object.
  const handleAcceptedFiles = files => {
    files.map(file => {
      Object.assign(file, {
        preview: URL.createObjectURL(file),
        formattedSize: ImageUtils.formatBytes(file.size)
      });
      return encodeFileBase64(file);
    });
  };

  // convert Base64 funct.
  const encodeFileBase64 = file => {
    const reader = new FileReader();
    if (file) {
      reader.readAsDataURL(file);
      reader.onload = () => {
        let [mimeType, ...Base64] = reader.result.split(',');
        // const Base64 = reader.result;
        setFieldValue(`${name}`, ...Base64);
      };
      reader.onerror = error => {
        console.log('base64 error ', error);
      };
    }
  };

  // rejected files
  const fileRejectionItems = fileRejections.map(({ file, errors }) => {
    const errorCode = errors.map(e => e.code);
    const showErrorMessage = () => {
      switch (errorCode[0]) {
        case 'too-many-files':
          return 'En fazla 1 fotoğraf ekleyebilirsiniz!';
        case 'file-too-large':
          return "Fotoğraf boyutu 5MB'dan büyük olamaz!";
        case 'file-invalid-type':
          return 'Yüklemek istediğiniz fotoğrafın türü (.png, .jpeg, .jpg) olmalıdır.';
        default:
          return 'Hatalı bir işlem yapıldı. (İşlem Başarısız)';
      }
    };
    return (
      <div key={errorCode[0]}>
        <span className="text-red-500 font-semibold mt-12 flex items-end">
          <i className={`bx bx-error bx-md mr-4`}></i>
          {showErrorMessage()}
        </span>
      </div>
    );
  });

  // remove photo while waiting upload or after
  const removePhotos = e => {
    e.preventDefault();
    setSelectedFiles([]);
    setFieldValue(name, null);
    removeFile && removeFile();
  };

  return (
    <>
      <InputLabel htmlFor={name} className="mb-8 text-gray-700 font-semibold">
        {label}
      </InputLabel>
      <>
        <div
          className={clsx(
            invalid ? ' border-red-400 text-red-400' : 'border-gray-400',
            'min-h-128 border-3 border-dashed  rounded-6'
          )}>
          <div className="w-full text-center p-20  mt-8" {...getRootProps()}>
            <input
              {...props}
              {...getInputProps()}
              accept=".jpeg, .jpg, .png"
              id={`id_${name}`}
              name={name}
              disabled={loading}
              ref={inputRef}
            />
            <div className="mb-6">
              <Icon
                fontSize="large"
                className={clsx(invalid ? 'text-red-500' : 'text-gray-600 ', 'text-40')}>
                backup
              </Icon>
              {/* <i className="text-gray-500 bx bxs-cloud-upload text-32" /> */}
            </div>
            <h5 className={clsx(invalid ? 'text-red-500' : 'text-gray-600 ', 'text-15')}>
              Fotoğrafı bu alana sürükleyerek ya da tıklayarak yükleyebilirsiniz...
            </h5>
          </div>
        </div>
        {invalid && <div className="text-red-500 text-12 mx-4 mt-4">{meta.error}</div>}

        {/* Image Preview */}
        {selectedFiles?.map((f, i) => {
          return (
            <div key={i + '-file'} className="mt-16" id="file-previews">
              <Card className="mt-8 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete">
                <div className="flex items-start justify-between flex-nowrap p-8">
                  <div className="flex items-center justify-between mr-12">
                    {!!loading && (
                      <div className="mx-8 flex items-center justify-center">
                        <CircularProgress color="primary" size={20} thickness={3} />
                      </div>
                    )}

                    <div className="px-8">
                      <img
                        height={90}
                        className="max-h-96 rounded-8 bg-gray-200 "
                        alt={f?.name}
                        src={f?.preview}
                      />
                    </div>
                  </div>

                  <div className="flex flex-1 flex-col">
                    <Typography variant="body1" className="text-gray-600 break-all font-bold mb-0">
                      {f.name}
                    </Typography>
                    <Typography variant="body2" className="mb-0 font-extrabold">
                      {f?.formattedSize}
                    </Typography>
                  </div>

                  <div className="flex justify-end pr-8">
                    <Link to="#" className="text-right font-bold" onClick={removePhotos}>
                      <i className={`bx bx-x bx-md text-gray-600`}></i>
                    </Link>
                  </div>
                </div>
              </Card>
            </div>
          );
        })}

        {/* Rejected Files and Error Messages */}
        {fileRejectionItems[0] || null}
      </>
    </>
  );
};

export default AddPhoto;
